import React, { useEffect, useRef } from "react";
import { Box } from "@chakra-ui/react";
import { textToMarkdown } from "@root/utils/streaming";
import {
  ENGLISH_TRANSLATION,
  ORIGINAL_TEXT_KOREAN,
  RISK_2_KOREAN,
  RISK_CELL,
  RISK_ENGLISH,
  RISK_KOREAN,
} from "@root/constants/common";

interface MarkdownContentProps {
  content: string;
  p?: number;
  onLinkClick?: (text: string, url: string, event?: MouseEvent) => void;
  sources?: any;
  disableLinks?: boolean;
  styleCitations?: boolean;
  colorRiskCells?: boolean;
}

const MarkdownContent: React.FC<MarkdownContentProps> = ({
  content,
  p = 4,
  onLinkClick,
  sources,
  disableLinks = false,
  styleCitations = false,
  colorRiskCells = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onLinkClick || !containerRef.current) return;

    const handleLinkClick = (event: Event) => {
      // Prevent link clicks when links are disabled
      if (disableLinks) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const target = event.target as HTMLElement;
      const mouseEvent = event as MouseEvent;

      // Check if the clicked element is a link or inside a link
      const link = target.closest("a");
      if (link) {
        event.preventDefault();
        event.stopPropagation();

        const text = link.textContent || "";
        const url = link.getAttribute("href") || "";

        onLinkClick(text, url, mouseEvent);
      }
    };

    const container = containerRef.current;
    container.addEventListener("click", handleLinkClick);

    return () => {
      container.removeEventListener("click", handleLinkClick);
    };
  }, [onLinkClick, disableLinks]);

  // Color the Risk column cells based on value thresholds
  useEffect(() => {
    if (!colorRiskCells || !containerRef.current) return;

    const container = containerRef.current;

    // Clear previously styled cells
    container.querySelectorAll("td.risk-colored").forEach((td) => {
      (td as HTMLElement).style.backgroundColor = "";
      td.classList.remove("risk-colored");
    });

    const tables = Array.from(container.getElementsByTagName("table"));
    tables.forEach((table) => {
      const thead = table.querySelector("thead");
      const headerRow = thead?.querySelector("tr");
      const headers = Array.from(headerRow?.querySelectorAll("th") || []);
      if (headers?.length === 0) return;

      // find all Risk columns (some tables may have before/after Risk columns)
      const riskColIndices = headers
        ?.map((th, idx) => ({
          idx,
          text: (th?.textContent || "")?.trim()?.toLowerCase(),
        }))
        ?.filter(
          ({ text }) =>
            text === RISK_ENGLISH ||
            text === RISK_KOREAN ||
            text === RISK_2_KOREAN
        )
        ?.map(({ idx }) => idx);

      if (riskColIndices?.length === 0) return;

      const bodyRows = Array.from(table.querySelectorAll("tbody tr"));
      bodyRows?.forEach((row) => {
        const cells = Array.from(row?.querySelectorAll("td"));
        riskColIndices.forEach((colIdx) => {
          const cell = cells[colIdx] as HTMLElement | undefined;
          if (!cell) return;
          const numeric = parseFloat((cell?.textContent || "")?.trim());
          if (Number.isNaN(numeric)) return;

          let bg = "";
          if (numeric <= RISK_CELL.LOW_MAX) bg = RISK_CELL.COLORS.GREEN;
          else if (numeric > RISK_CELL.LOW_MAX && numeric < RISK_CELL.HIGH_MIN)
            bg = RISK_CELL.COLORS.YELLOW;
          else if (numeric >= RISK_CELL.HIGH_MIN) bg = RISK_CELL.COLORS.RED;

          if (bg) {
            cell.style.backgroundColor = bg;
            cell.classList.add("risk-colored");
          }
        });
      });
    });
  }, [content, colorRiskCells]);

  // Style all consecutive <p> tags after "English Translation:" and "Original Text (Korean):" paragraphs
  useEffect(() => {
    if (!styleCitations || !containerRef.current) return;

    const container = containerRef.current;

    // Find all <p> tags
    const paragraphs = Array?.from?.(container?.querySelectorAll("p")) || [];

    paragraphs?.forEach?.((p) => {
      const text = p?.textContent || "";

      // Check if this paragraph contains "English Translation:"
      if (text?.includes(ENGLISH_TRANSLATION)) {
        // Style all consecutive <p> tags after this one
        let nextElement = p?.nextElementSibling;
        while (nextElement && nextElement?.tagName?.toLowerCase() === "p") {
          const nextText = nextElement?.textContent || "";
          // Stop if we encounter another header
          if (
            nextText?.includes(ENGLISH_TRANSLATION) ||
            nextText?.includes(ORIGINAL_TEXT_KOREAN)
          ) {
            break;
          }
          const element = nextElement as HTMLElement;
          element.style.backgroundColor = "#F5F5F5";
          element.style.borderLeft = "4px solid #D1D5DB";
          element.style.borderRight = "none";
          element.style.padding = "12px 16px";
          element.style.marginBottom = "12px";
          element.style.borderRadius = "4px";
          nextElement = nextElement.nextElementSibling;
        }
      }

      // Check if this paragraph contains "Original Text (Korean):"
      if (text?.includes(ORIGINAL_TEXT_KOREAN)) {
        // Style all consecutive <p> tags after this one
        let nextElement = p?.nextElementSibling;
        while (nextElement && nextElement?.tagName?.toLowerCase() === "p") {
          const nextText = nextElement?.textContent || "";
          // Stop if we encounter another header
          if (
            nextText?.includes(ENGLISH_TRANSLATION) ||
            nextText?.includes(ORIGINAL_TEXT_KOREAN)
          ) {
            break;
          }
          const element = nextElement as HTMLElement;
          element.style.backgroundColor = "#FAF0E6";
          element.style.borderLeft = "4px solid #F6C23A";
          element.style.borderRight = "none";
          element.style.padding = "12px 16px";
          element.style.marginBottom = "12px";
          element.style.borderRadius = "4px";
          nextElement = nextElement.nextElementSibling;
        }
      }
    });
  }, [content, styleCitations]);

  return (
    <Box
      p={p}
      sx={{
        "& a": {
          cursor: disableLinks ? "not-allowed" : "pointer",
          opacity: disableLinks ? 0.6 : 1,
        },
      }}
    >
      <div
        ref={containerRef}
        className="htmltargeted"
        dangerouslySetInnerHTML={{ __html: textToMarkdown(content) }}
      />
    </Box>
  );
};

export default MarkdownContent;
